/**
 * What's on your plate — the list screen's read and its three writes (issue #310).
 *
 * **Four endpoints and no fifth.** `GET /backlog` is #299's paged read — the whole list,
 * closed items included, which is why the screen can offer *Finished* and *Everything* at all.
 * `POST /resolve`, `POST /update` and `POST /delete-item` are #298's routes onto the tools
 * themselves.
 *
 * **There is no fifth because the fifth would be `/capture`.** Nothing on this screen creates
 * an item, which is the design rather than an omission: items arrive from what the family says,
 * classifying an utterance is the host model's job, and the server calls no completions API —
 * so a panel could only create by making somebody fill in a form. `writes.ts`'s
 * {@link WriteTail} does not name that route, so this file could not send one if it tried.
 *
 * **Three acts, three tools, named as three things.** *Done* is `resolve`, *Edit* is `update`,
 * *Forget* is `delete_item` — the same plain functions, the same tiers, the same `PROPOSAL#`
 * and the same `ACTION#` as a conversation reaches. Collapsing any two of them is how somebody
 * loses what they meant to keep or keeps what they meant to lose, which is the whole argument
 * #309 had for `delete_item` existing beside `resolve` in the first place.
 *
 * **Nothing here decides a tier, and nothing here decides whether to ask.** `resolve` and
 * `update` carry no `provenance` field (ADR 0014 decision 4b), so they are always inferred and
 * under the default `ask_always` they propose; a field edit under `ai_write_mode: auto_low_risk`
 * applies in one call; an owner change is medium and asks either way; `delete_item` always
 * proposes. The screen sends a write and renders whichever envelope came back — see
 * `memory.ts` for why predicting it would be a second copy of `confirmation.ts` in a browser.
 *
 * **The response types are derived from the server's routes, not declared here** — ADR 0025
 * decision 3. This file used to say the opposite: that the type is deliberately a *subset* of
 * what the server discloses, because a field the screen does not render is a field it should
 * not claim to understand.
 *
 * That rule is retired, and the reason is the one this whole change is about. A subset is a
 * second, unchecked statement of the contract — right until the server renames something
 * inside it, at which point this file goes on compiling and renders `undefined`. Narrowing
 * what a *screen* uses is the screen's job; narrowing what the *contract* says only hides
 * drift.
 */

import type { FamilyPayloads } from "@pilotfish/api/src/app/contract.ts";
import { type AppApi, appFetch, familyPath, postWrite } from "@pilotfish/app-api";

/**
 * **What's on the plate, as the server answers it** — `FamilyPayloads["GET /backlog"]`, derived
 * from `reads.ts` rather than described here (ADR 0025 decisions 3 and 4).
 *
 * The whole list, closed items included: this route is AP8 without its `status` filter, so the
 * screen's filters narrow what it *shows* rather than what it asks for.
 */
export type PlatePage = FamilyPayloads["GET /backlog"];

/**
 * One thing on the family's list, as `disclosure.ts` shows it to the family it belongs to.
 *
 * **`rawText` is declared, because the server sends it.** It did not used to be: this file
 * withheld it on the retired subset rule the header describes. `readBacklog` maps through
 * `discloseItem`, so `DisclosedItem` carries the family's own words and this type now says so.
 *
 * Nothing about what the *screen* does changes — the canvas renders a title, whose it is and
 * when it is due, and never `rawText`. What changes is that the omission is now a rendering
 * decision in `Plate.tsx` rather than a claim about the wire, which is where it belonged: a
 * type that lied about the payload was never the thing keeping those words off the row.
 */
export type PlateItem = PlatePage["records"][number];

/** What closing something out answers with. */
export type ResolveEnvelope = FamilyPayloads["POST /resolve"];

/** What correcting an item answers with. */
export type UpdateEnvelope = FamilyPayloads["POST /update"];

/**
 * One page of the list.
 *
 * **`limit` is stated rather than defaulted** (PR #336 review, finding 5). `pageRequest` omits
 * an absent one and the accessor falls back to `DEFAULT_PAGE_SIZE` — fifty — so a walk over a
 * long list took four times the round trips it needed, each one a Lambda invoke behind
 * CloudFront, on the one screen whose design is *read all of it before rendering anything*.
 * `pageSize` clamps at `MAX_PAGE_SIZE`, so asking for the ceiling is answered rather than
 * refused, and a short page is not a lost record: the cursor is followed either way.
 *
 * **It is a page size, never a filter.** The screen narrows what it renders and never what it
 * reads (`Plate.tsx`), and `tests/plate.test.tsx` holds that against the query string — which
 * is why `limit` is the one other parameter that assertion allows.
 *
 * Restored after review: this block sat above the function and went out with the `PlatePage`
 * interface it happened to follow. The derivation changed what the *types* say, not why the
 * request is shaped this way — and the next reader of this signature has a plausible
 * simplification (drop `limit`, let the server default) that a review already rejected once,
 * with a measurement.
 */
export function fetchBacklogPage(
  api: AppApi,
  familyId: string,
  after: string | null,
  limit: number,
): Promise<PlatePage> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (after !== null) query.set("after", after);
  return appFetch<PlatePage>(api, familyPath(familyId, `/backlog?${query.toString()}`));
}

/**
 * **What a delete answers with** — `FamilyPayloads["POST /delete-item"]`, which is
 * `delete_item`'s own output type. The panel and an MCP host read one type here, which is the
 * strongest form this could take: a write route's payload *is* its tool's return type.
 */
export type DeleteItemEnvelope = FamilyPayloads["POST /delete-item"];

/**
 * What `delete_item` is about to take, in the family's terms (#309).
 *
 * `quotedOnFacts` is the disclosure that matters most: it announces that something the family
 * asked Boone to *remember* is being edited as a side effect of deleting something off the
 * list. The sentence a person actually reads is the server's own `message`, which builds this
 * in — the panel neither assembles it nor may.
 */
export type DeleteTarget = NonNullable<DeleteItemEnvelope["deleting"]>;

/**
 * Close something out — **done, or dropped**, and the screen only ever sends the first.
 *
 * `resolve` takes both; *Done* is the canvas's one control and it means what it says (*"it
 * happened. It leaves this list and stays in the record"*). Dropping something as no longer
 * relevant is a different sentence, and on this screen it is covered by the act next to it:
 * *Forget* removes it outright, which is what somebody who never wanted it there is after.
 * Offering a third button for the state in between is how three clear acts become four
 * ambiguous ones.
 */
export function proposeResolve(
  api: AppApi,
  familyId: string,
  itemId: string,
  idempotencyKey: string,
): Promise<ResolveEnvelope> {
  return postWrite(api, familyId, "/resolve", {
    itemId,
    resolution: "done",
    idempotencyKey,
  });
}

/**
 * What the edit form can change — `update`'s own fields, minus the ones this screen does not
 * show.
 *
 * **`priority` is deliberately absent**, and it is the one omission worth arguing. `update`
 * accepts it and the rank sorts on it, so a control would be easy — and it would be the only
 * thing on this page a family maintains for the sake of the page. #310 rules out *"drag,
 * reorder, manual ranking"* because grouping is `timeHorizon` and not a view anyone keeps
 * tidy, and a priority picker is that rule with a dropdown instead of a drag handle. Nothing
 * on the row displays it either, so the control would be the family's only evidence it exists.
 * Boone still sets it from what they say.
 *
 * **An absent field means "leave it alone" and never "clear it"** — the encoding #134 chose,
 * so retraction is the explicit {@link ItemPatch.clear} list.
 */
export interface ItemPatch {
  readonly title?: string;
  readonly timeHorizon?: string;
  readonly dueDate?: string;
  readonly ownerMemberId?: string;
  /** `["dueDate"]` for no date after all, `["ownerMemberId"]` for nobody after all. */
  readonly clear?: readonly ("dueDate" | "ownerMemberId")[];
}

export function proposeUpdate(
  api: AppApi,
  familyId: string,
  itemId: string,
  patch: ItemPatch,
  idempotencyKey: string,
): Promise<UpdateEnvelope> {
  return postWrite(api, familyId, "/update", { itemId, ...patch, idempotencyKey });
}

/**
 * Delete one thing off the list, for good (#309).
 *
 * **Any member may call it and there is no capability to check here** — settled explicitly by
 * that issue, because the case it exists for is *"I put that in, I want it gone"* and requiring
 * an owner would mean the person who made the mistake cannot fix their own slip. It always
 * proposes: there is no `provenance` field on the tool, so no wording and no `ai_write_mode`
 * deletes an item in one turn.
 */
export function proposeDelete(
  api: AppApi,
  familyId: string,
  itemId: string,
  idempotencyKey: string,
): Promise<DeleteItemEnvelope> {
  return postWrite<DeleteItemEnvelope>(api, familyId, "/delete-item", { itemId, idempotencyKey });
}
